import React, { useState, useEffect } from 'react';
import { RadioGroup } from 'src/common/form/radio-group';
import { MultiFileUploadForm } from 'src/editor/content-creation/components/multi-file-upload-form';
import * as Yup from 'yup';
import { apiUploadResource } from 'src/utils/journeyApi';

interface CreateJourneyAIAssistantProps {
  onChange: (field: string, value: any) => void;
  onSubmit: (values: Record<string, any>) => void;
}

export const AssistantValidationSchema = Yup.object({
  journeyOption: Yup.string().required(),
  articleUrl: Yup.string(),
  assistantComplete: Yup.boolean().oneOf([true], 'Complete the assistant wizard to continue.')
});

// FileIcon component for inline SVGs based on file extension
const FileIcon: React.FC<{ fileType: string, fileName: string }> = ({ fileType, fileName }) => {
  const lowerName = fileName.toLowerCase();
  if(lowerName.endsWith('.pdf')) {
    return (
      <svg width="20" height="20" viewBox="0 0 512 512" fill="#E74C3C" xmlns="http://www.w3.org/2000/svg">
        <path d="M369.9 97.98L314.02 42.1C305.3 33.38 293.1 27.99 280.05 27.99H48C21.49 27.99 0 49.48 0 75.99v360.02C0 462.5 21.49 484 48 484h416c26.51 0 48-21.49 48-47.99V132.05c0-13.05-5.39-25.25-14.1-34.07zM352 121V48l112 112H375.1c-6.2 0-12.1-2.5-16.1-6.93L352 121z"/>
      </svg>
    );
  } else if(lowerName.endsWith('.mp4') || lowerName.endsWith('.avi') || lowerName.endsWith('.mov')) {
    return (
      <svg width="20" height="20" viewBox="0 0 576 512" fill="#E67E22" xmlns="http://www.w3.org/2000/svg">
        <path d="M336.2 64H48C21.5 64 0 85.5 0 112v288c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V175.8L336.2 64zM384 288c0 17.7-14.3 32-32 32s-32-14.3-32-32V208c0-17.7 14.3-32 32-32s32 14.3 32 32v80z"/>
      </svg>
    );
  } else if(lowerName.endsWith('.mp3') || lowerName.endsWith('.wav')) {
    return (
      <svg width="20" height="20" viewBox="0 0 192 512" fill="#9B59B6" xmlns="http://www.w3.org/2000/svg">
        <path d="M96 0C42.98 0 0 42.98 0 96v320c0 53.02 42.98 96 96 96s96-42.98 96-96V96C192 42.98 149.02 0 96 0zm32 416c0 17.67-14.33 32-32 32s-32-14.33-32-32V96c0-17.67 14.33-32 32-32s32 14.33 32 32v320z"/>
      </svg>
    );
  } else if(lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) {
    return (
      <svg width="20" height="20" viewBox="0 0 384 512" fill="#3498DB" xmlns="http://www.w3.org/2000/svg">
        <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.3 0-24-10.7-24-24zM377.9 105.9L278.1 6.1C273.5 1.5 266.7 0 260 0h-1v128h128v-2.1c0-6.7-1.5-13.5-6.1-18z"/>
      </svg>
    );
  } else if(lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx')) {
    return (
      <svg width="20" height="20" viewBox="0 0 384 512" fill="#27AE60" xmlns="http://www.w3.org/2000/svg">
        <path d="M384 32v448c0 17.7-14.3 32-32 32H32C14.3 512 0 497.7 0 480V32C0 14.33 14.33 0 32 0h320C369.7 0 384 14.33 384 32zM224 128H112c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16h112c8.8 0 16-7.2 16-16V144C240 135.2 232.8 128 224 128zM336 128h-48c-8.8 0-16 7.2-16 16v224c0 8.8 7.2 16 16 16h48c8.8 0 16-7.2 16-16V144C352 135.2 344.8 128 336 128z"/>
      </svg>
    );
  } else if(lowerName.endsWith('.ppt') || lowerName.endsWith('.pptx')) {
    return (
      <svg width="20" height="20" viewBox="0 0 384 512" fill="#E74C3C" xmlns="http://www.w3.org/2000/svg">
        <path d="M369.9 97.98L314.02 42.1C305.3 33.38 293.1 27.99 280.05 27.99H48C21.49 27.99 0 49.48 0 75.99v360.02C0 462.5 21.49 484 48 484h416c26.51 0 48-21.49 48-47.99V132.05c0-13.05-5.39-25.25-14.1-34.07zM352 121V48l112 112H375.1c-6.2 0-12.1-2.5-16.1-6.93L352 121z"/>
      </svg>
    );
  } else {
    return (
      <svg width="20" height="20" viewBox="0 0 384 512" fill="#BDC3C7" xmlns="http://www.w3.org/2000/svg">
        <path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.3 0-24-10.7-24-24zM377.9 105.9L278.1 6.1C273.5 1.5 266.7 0 260 0h-1v128h128v-2.1c0-6.7-1.5-13.5-6.1-18z"/>
      </svg>
    );
  }
};

const inlineStyles = `
.fade-in {
  animation: fadeInSlideIn 0.5s ease-out both;
}

@keyframes fadeInSlideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
`;

export const CreateJourneyAIAssistant: React.FC<CreateJourneyAIAssistantProps> = ({ onChange, onSubmit }) => {
  const [step, setStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState("Sales Call/Demo Follow-up");
  const [prompt, setPrompt] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string, type: string, key?: string, progress?: number }>>([]);
  const [includeImages, setIncludeImages] = useState(true);
  const [websiteUrls, setWebsiteUrls] = useState<string[]>([""]);

  useEffect(() => {
    onChange('assistantComplete', step === 3);
  }, [step, onChange]);

  const acceptedFileTypes: Record<string, JSX.Element> = {
    "Sales Call/Demo Follow-up": (
      <ul className="ml-4 list-disc">
        <li>Call recordings (audio or video)</li>
        <li>Meeting notes (text files, PDFs, or markdown)</li>
        <li>Sales decks or presentation slides (PDFs, PowerPoints)</li>
      </ul>
    ),
    "Lead Magnet": (
      <ul className="ml-4 list-disc">
        <li>E-books, guides, or whitepapers (PDFs, Word docs)</li>
        <li>Blog articles or website pages (URLs or text)</li>
        <li>Video files (MP4, MOV) or transcripts (text)</li>
      </ul>
    ),
    "Customer Story/Case Study": (
      <ul className="ml-4 list-disc">
        <li>Interview recordings or transcripts (audio/video or text)</li>
        <li>Customer-provided documents (PDFs, Word docs)</li>
        <li>Performance metrics and screenshots (images, CSV files)</li>
      </ul>
    )
  };

  const handleNewFile = async (fileOrFiles: File | File[]) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    for (const file of files) {
      try {
        const response = await apiUploadResource({ filename: file.name, content_type: file.type });
        const { url, key } = response;
        const fileObj = { name: file.name, type: file.type, key: undefined, progress: 0 };
        setUploadedFiles(prev => [...prev, fileObj]);

        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', url);
          xhr.setRequestHeader('Content-Type', file.type);
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percent = Math.round((event.loaded / event.total) * 100);
              setUploadedFiles(prev => prev.map(f => f.name === file.name ? { ...f, progress: percent } : f));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadedFiles(prev => {
                const updated = prev.map(f => f.name === file.name ? { ...f, key, progress: 100 } : f);
                onChange('resources', updated);
                return updated;
              });
              resolve();
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error('Network error during file upload'));
          xhr.send(file);
        });
      } catch (err) {
        console.error('Error uploading file:', file.name, err);
      }
    }
  };

  return (
    <>
      <style>{inlineStyles}</style>
      <div className="w-full -mt-5">
        {step === 0 && (
          <div key="step0" className="fade-in mt-0">
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-4">Select Journey Type</h2>
              <p className="mb-2">Choose the type of journey you want to create:</p>
              <RadioGroup
                name="journeyOption"
                className="flex flex-col space-y-sm text-white"
                itemClassName="flex gap-4 items-center"
                darkMode={true}
                cyanMode={true}
                options={[
                  { label: "Sales Call/Demo Follow-up", value: "Sales Call/Demo Follow-up" },
                  { label: "Lead Magnet", value: "Lead Magnet" },
                  { label: "Customer Story/Case Study", value: "Customer Story/Case Study" }
                ]}
                value={selectedOption}
                onChange={(option) => {
                  setSelectedOption(option.value);
                  onChange('journeyOption', option.value);
                  // Auto advance to next step after a short delay
                  setTimeout(() => setStep(1), 300);
                }}
              />
              <div className="flex justify-end mt-8">
                <button onClick={() => setStep(1)} className="flex items-center text-lg text-gray-300 hover:text-white">
                  <span>Next</span>
                  <span className="ml-1">→</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {step === 1 && (
          <div key="step1" className="fade-in mt-0">
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-4">Upload Resources</h2>
              {/* <p className="mb-2">Upload resources (accepted types vary based on selection):</p> */}
              <div className="mb-2 text-sm text-gray-300">
                {acceptedFileTypes[selectedOption]}
              </div>
              <MultiFileUploadForm
                containerClassName="h-20 w-40 p-0"
                label="Upload resource"
                onNewFile={handleNewFile}
              />
              {uploadedFiles.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {uploadedFiles.map((file, idx) => (
                    <li key={idx} className="text-sm flex flex-col gap-1 p-2 border rounded shadow-sm bg-gray-800 border-gray-700 text-white">
                      <div className="flex items-center gap-2">
                        <FileIcon fileType={file.type} fileName={file.name} />
                        <span>{file.name}</span>
                      </div>
                      {file.progress !== undefined && file.progress < 100 && (
                        <div className="flex items-center gap-2">
                          <progress className="w-32" value={file.progress} max="100" />
                          <span>{file.progress}%</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-between mt-8">
                <button onClick={() => setStep(0)} className="flex items-center text-lg text-gray-300 hover:text-white">
                  <span>←</span>
                  <span className="ml-1">Back</span>
                </button>
                <button onClick={() => setStep(2)} className="flex items-center text-lg text-gray-300 hover:text-white">
                  <span>Next</span>
                  <span className="ml-1">→</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div key="step2" className="fade-in mt-0">
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-4">Enter Website URLs</h2>
              <p className="mb-4 text-gray-300">Provide the URLs of websites relevant to your journey. You can add multiple URLs.</p>
              {websiteUrls.map((url, index) => (
                <div key={index} className="mb-3">
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => {
                      const newUrls = [...websiteUrls];
                      newUrls[index] = e.target.value;
                      setWebsiteUrls(newUrls);
                      onChange('websiteUrls', newUrls);
                    }}
                    placeholder="Enter website URL"
                    className="w-full p-2 rounded bg-gray-700 text-white placeholder-gray-400 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ))}
              <button
                onClick={() => {
                  const newUrls = [...websiteUrls, ""];
                  setWebsiteUrls(newUrls);
                  onChange('websiteUrls', newUrls);
                }}
                className="mt-2 text-sm px-4 py-2 bg-gray-700 shadow-md hover:bg-gray-800 rounded text-white flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add more website URL
              </button>
              <div className="flex justify-between mt-8">
                <button onClick={() => setStep(1)} className="flex items-center text-lg text-gray-300 hover:text-white">
                  <span>←</span>
                  <span className="ml-1">Back</span>
                </button>
                <button
                  onClick={() => {
                    setStep(3);
                    onChange('assistantComplete', true);
                  }}
                  className="flex items-center text-lg text-gray-300 hover:text-white"
                >
                  <span>Next</span>
                  <span className="ml-1">→</span>
                </button>
              </div>
            </div>
          </div>
        )}
        {step === 3 && (
          <div key="step3" className="fade-in mt-0">
            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-semibold mb-4">Settings</h2>
              <div className="flex items-center mb-8">
                <input
                  type="checkbox"
                  id="includeImages"
                  checked={includeImages}
                  onChange={(e) => {
                    setIncludeImages(e.target.checked);
                    onChange('imageUse', e.target.checked);
                  }}
                  className="form-checkbox h-5 w-5 text-blue-500 bg-gray-700 border-gray-600 mr-2 cursor-pointer"
                />
                <label htmlFor="includeImages" className="cursor-pointer">Include Images in the Journey</label>
              </div>
              <div className="mb-4">
                <label htmlFor="promptDetail" className="mb-1 block">Additional Details for the Content</label>
                <textarea
                  id="promptDetail"
                  className="w-full p-2 rounded text-sm bg-gray-700 text-white placeholder-gray-400 border border-gray-600"
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    onChange('prompt', e.target.value);
                  }}
                  placeholder="Add more details about what you want to add in your journey"
                />
              </div>
              <div className="flex justify-start mt-8">
                <button onClick={() => setStep(2)} className="flex items-center text-lg text-gray-300 hover:text-white">
                  <span>←</span>
                  <span className="ml-1">Back</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}; 